import { useNavigate, useParams, Link } from 'react-router-dom';
import { ChevronLeft, Package } from 'lucide-react';
import { useProduct } from '../hooks/useProducts.js';
import { formatCurrency, formatDate } from '../lib/format.js';

const STATUS_BADGE = {
  Live:    { label: 'Live',    className: 'bg-green-100 text-green-700' },
  Pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
};

function StatusBadge({ status }) {
  const conf = STATUS_BADGE[status] ?? { label: status ?? '—', className: 'bg-slate-100 text-slate-500' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${conf.className}`}>
      {conf.label}
    </span>
  );
}

function InfoRow({ label, value }) {
  if (value == null || value === '') return null;
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-500 shrink-0">{label}</span>
      <span className="text-sm text-slate-900 text-right">{value}</span>
    </div>
  );
}

export default function ProductDetailPage() {
  const { sku } = useParams();
  const navigate = useNavigate();
  const decodedSku = sku ? decodeURIComponent(sku) : '';
  const { data, isLoading, error } = useProduct(decodedSku);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-32 bg-slate-100 animate-pulse rounded" />
        <div className="h-40 bg-slate-100 animate-pulse rounded-xl" />
        <div className="h-32 bg-slate-100 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => navigate('/ventes/produits')}
          className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ChevronLeft size={16} aria-hidden /> Retour
        </button>
        <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error.status === 404 ? 'Produit introuvable.' : `Erreur : ${error.message}`}
        </div>
      </div>
    );
  }

  const product = data?.product;
  const recentOrders = data?.recent_orders ?? [];

  if (!product) return null;

  return (
    <div className="space-y-4">
      {/* Bouton retour */}
      <button
        type="button"
        onClick={() => navigate('/ventes/produits')}
        className="flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 transition-colors"
      >
        <ChevronLeft size={16} aria-hidden /> Retour aux produits
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4 space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs font-mono text-slate-500">{product.sku}</p>
            {product.identification_id && (
              <p className="text-[11px] font-mono text-slate-400 mt-0.5">ID {product.identification_id}</p>
            )}
            <h2 className="text-lg font-bold text-slate-900 mt-0.5 leading-snug">{product.name ?? '—'}</h2>
          </div>
          <StatusBadge status={product.status} />
        </div>
        {product.price_cents != null && (
          <p className="text-2xl font-bold tabular-nums text-slate-900">{formatCurrency(product.price_cents)}</p>
        )}
      </div>

      {/* Informations */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-2">Informations</h3>
        <div>
          <InfoRow label="Identifiant unique" value={product.identification_id} />
          <InfoRow label="Référence OEM" value={product.reference} />
          <InfoRow label="Type" value={product.product_type} />
          <InfoRow label="Collection" value={product.collection_name} />
          <InfoRow label="État" value={product.state_of_product} />
          <InfoRow label="Condition" value={product.item_condition} />
          <InfoRow
            label="Prix de vente"
            value={product.price_cents != null ? formatCurrency(product.price_cents) : null}
          />
          {product.compare_at_cents != null && (
            <InfoRow label="Prix barré" value={formatCurrency(product.compare_at_cents)} />
          )}
          <InfoRow label="Quantité disponible" value={product.available_quantity} />
          <InfoRow label="Quantité totale" value={product.total_quantity} />
          <InfoRow label="Date inventaire" value={formatDate(product.inventory_date)} />
          <InfoRow label="Date publication" value={formatDate(product.publish_date)} />
        </div>
      </div>

      {/* Historique des ventes */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-4 py-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-3">Historique des ventes</h3>

        {recentOrders.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center">
            <Package size={28} className="text-slate-300" />
            <p className="text-sm text-slate-500">Aucune vente enregistrée pour ce SKU.</p>
          </div>
        ) : (
          <>
            {/* Mobile cards */}
            <div className="space-y-2 md:hidden">
              {recentOrders.map((row) => (
                <Link
                  key={row.order_id}
                  to={`/ventes/commandes/${row.order_id}`}
                  className="block border border-slate-100 rounded-lg px-3 py-2.5 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{row.order_name ?? `#${row.order_number}`}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{formatDate(row.created_at)} · {row.customer_email ?? '—'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums text-slate-900">×{row.quantity}</p>
                      <p className="text-xs text-slate-500">{formatCurrency(row.unit_price_cents)}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                    <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Commande</th>
                    <th className="text-left pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Client</th>
                    <th className="text-right pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Qté</th>
                    <th className="text-right pb-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">Prix u.</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {recentOrders.map((row) => (
                    <tr key={row.order_id} className="hover:bg-slate-50">
                      <td className="py-2.5 pr-4 text-slate-600 whitespace-nowrap">{formatDate(row.created_at)}</td>
                      <td className="py-2.5 pr-4">
                        <Link
                          to={`/ventes/commandes/${row.order_id}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          {row.order_name ?? `#${row.order_number}`}
                        </Link>
                      </td>
                      <td className="py-2.5 pr-4 text-slate-600 max-w-[180px] truncate">{row.customer_email ?? '—'}</td>
                      <td className="py-2.5 pr-4 text-right tabular-nums text-slate-700">{row.quantity}</td>
                      <td className="py-2.5 text-right tabular-nums font-semibold text-slate-900">
                        {formatCurrency(row.unit_price_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
